"use client";

import { useIsMobile } from "@/hooks/use-mobile";
import {
  GAME_THEATER_PLAYER_BACKGROUND,
  GAME_THEATER_PLAYER_BG_CLASS,
} from "@/lib/constants/layout";
import { forwardRef, useImperativeHandle, useRef } from "react";

interface VideoPlayerProps {
  videoUrl: string;
  title: string;
  /** Fills parent height (game page theater layout) instead of fixed 16:9 aspect ratio. */
  fillContainer?: boolean;
}

export interface VideoPlayerRef {
  pause: () => void;
}

// Function to add autoplay to video URLs when needed
function addAutoplayToUrl(url: string): string {
  if (url.includes("player.vimeo.com")) {
    if (url.includes("autoplay=")) {
      return url.replace(/autoplay=[^&]*/, "autoplay=1");
    }
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}autoplay=1`;
  }

  return url;
}

export const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(
  ({ videoUrl, title, fillContainer = false }, ref) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const isMobile = useIsMobile();

    // Mobile browsers (especially iOS Safari) often force-muted playback when an embed is set to autoplay.
    // Let the user explicitly press play on mobile so audio is allowed.
    const autoplayUrl = isMobile ? videoUrl : addAutoplayToUrl(videoUrl);

    useImperativeHandle(ref, () => ({
      pause: () => {
        if (iframeRef.current) {
          const currentSrc = iframeRef.current.src;
          const pausedUrl = currentSrc
            .replace(/[?&]autoplay=1/, "")
            .replace(/[?&]autoplay=true/, "");
          iframeRef.current.src = pausedUrl;
        }
      },
    }));

    return (
      <div
        className={`VIDEOCONTAINER relative w-full overflow-hidden ${
          fillContainer
            ? `h-full rounded-none ${GAME_THEATER_PLAYER_BG_CLASS}`
            : isMobile
              ? "aspect-video rounded-none"
              : "aspect-video rounded-3xl"
        }`}
        style={fillContainer ? { backgroundColor: GAME_THEATER_PLAYER_BACKGROUND } : undefined}
      >
        <iframe
          ref={iframeRef}
          src={autoplayUrl}
          title={title}
          className="absolute inset-0 w-full h-full border-0"
          allowFullScreen
          allow="autoplay; fullscreen; picture-in-picture"
          frameBorder="0"
        />
      </div>
    );
  }
);

VideoPlayer.displayName = "VideoPlayer";
